import React, { useCallback, useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { CloudDownload, Loader2, RefreshCw, History, AlertTriangle, PauseCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { getAutoImportStatus, setAutoImportEnabled, runAutoImport } from '../../services/api';
import { AutoImportLog } from './AutoImportLog';

const ago = (iso) => formatDistanceToNow(new Date(iso), { addSuffix: true });
const sinceLabel = (d) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

const RunProgress = ({ run }) => {
  const pct = run?.pages_total ? Math.round((100 * run.pages_done) / run.pages_total) : 0;
  return (
    <div className="space-y-1" data-testid="auto-import-progress">
      <p className="text-[11px] text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5">
        <Loader2 className="h-3 w-3 animate-spin" />
        {run?.phase === 'sync' ? 'Syncing title and status changes from Ceipal' : run?.mode === 'catch_up' ? 'Catching up on Ceipal jobs' : 'Checking Ceipal for new jobs'}
        {run?.pages_total ? ` · page ${run.pages_done} of ${run.pages_total}` : '…'}
      </p>
      <div className="h-1 rounded-full bg-muted overflow-hidden">
        <div className="h-full bg-indigo-500 transition-[width] duration-500" style={{ width: `${Math.max(pct, 4)}%` }} />
      </div>
    </div>
  );
};

const StatusLine = ({ s }) => {
  if (!s.enabled) {
    return <p className="text-[11px] text-muted-foreground flex items-center gap-1.5" data-testid="auto-import-paused"><PauseCircle className="h-3 w-3" /> Paused — new Ceipal jobs are not imported</p>;
  }
  if (s.last_status === 'error') {
    return (
      <p className="text-[11px] text-red-600 dark:text-red-400 flex items-start gap-1.5" data-testid="auto-import-error">
        <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> Last check failed {ago(s.last_run_at)}: {s.last_error}
      </p>
    );
  }
  return (
    <p className="text-[11px] text-muted-foreground" data-testid="auto-import-last-run">
      {s.last_run_at ? `Last checked ${ago(s.last_run_at)} · ${s.last_imported} new${s.last_updated ? ` · ${s.last_updated} updated` : ''}` : 'First check runs within 15 minutes — or press Check now'}
    </p>
  );
};

export const AutoImportPanel = ({ regionKey }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showLog, setShowLog] = useState(false);

  const load = useCallback(async () => {
    try { setS(await getAutoImportStatus(regionKey)); } catch { /* admin only */ }
  }, [regionKey]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!s || (!s.running && !s.details_pending)) return undefined;
    const t = setTimeout(load, s.running ? 3000 : 15000);
    return () => clearTimeout(t);
  }, [s, load]);

  const toggle = async (enabled) => {
    try {
      setS(await setAutoImportEnabled(regionKey, enabled));
      toast.success(enabled ? 'Auto-import is on' : 'Auto-import paused');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update auto-import'); }
  };
  const checkNow = async () => {
    setBusy(true);
    try { setS(await runAutoImport(regionKey)); toast.success('Checking Ceipal for new jobs…'); } catch (e) { toast.error(e.response?.data?.detail || 'Could not start the check'); } finally { setBusy(false); }
  };

  if (!s) return null;
  return (
    <div className="rounded-lg border p-3 space-y-2.5" data-testid={`auto-import-panel-${regionKey}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold flex items-center gap-1.5"><CloudDownload className="h-3.5 w-3.5 text-indigo-500" /> Auto-import new jobs</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">Checks Ceipal every 15 min · every job created since {sinceLabel(s.since)}, any status</p>
        </div>
        <Switch checked={s.enabled} onCheckedChange={toggle} data-testid={`auto-import-toggle-${regionKey}`} />
      </div>
      {s.running ? <RunProgress run={s.current_run} /> : <StatusLine s={s} />}
      <p className="text-[11px] text-muted-foreground" data-testid={`auto-import-stats-${regionKey}`}>
        <span className="font-semibold text-foreground tabular-nums">{s.jobs_imported.toLocaleString()}</span> jobs imported
        {s.details_pending > 0 && ` · adding Ceipal details to ${s.details_pending.toLocaleString()}`}
      </p>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="h-7 text-xs gap-1" disabled={busy || s.running} onClick={checkNow} data-testid={`auto-import-run-${regionKey}`}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Check now
        </Button>
        <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => setShowLog(!showLog)} data-testid={`auto-import-log-toggle-${regionKey}`}>
          <History className="h-3 w-3" /> {showLog ? 'Hide log' : 'Import log'}
        </Button>
      </div>
      {showLog && <AutoImportLog regionKey={regionKey} refreshKey={`${s.last_run_at}-${s.running}`} />}
    </div>
  );
};

export default AutoImportPanel;
